import { SkuPricesResponse } from '@/types/interfaces/sku'
import { ServiceItem } from '@/types/interfaces/service-item'
import type { BookingOptions, SelectedPaymentOption } from '@/types/interfaces/booking_options'
import { CheckVerifiedIcon } from '@/components/Icons/CheckVerifiedIcon'
import { ShieldTickIcon } from '@/components/Icons/ShieldTickIcon'
import { formatCentsToDollars } from '@/lib/utilities'
import React from 'react'

export interface SkuPriceStrings {
    preferredPrice?: number
    primaryText?: string
    secondaryPrice?: number
    secondaryPriceText?: string
    /** Original response used to compute these display fields */
    originalSkuPrices: SkuPricesResponse
}

/**
 * Mutates a ServiceItem by adding `primaryPrice` / `secondaryPrice`
 * strings derived from the SkuPricesResponse.
 */
export function enrichServiceItem(serviceItem: ServiceItem, prices: SkuPricesResponse, authenticated = false) {
    const skuPrice = calculateSkuPrices(prices, authenticated)

    serviceItem.primaryPrice = formatPrice(skuPrice.preferredPrice, skuPrice.primaryText)
    serviceItem.secondaryPrice = formatPrice(skuPrice.secondaryPrice, skuPrice.secondaryPriceText)
}

/**
 * Decide which of the two numbers (cash / insurance) should be displayed
 * first and craft the helper copy that accompanies it.
 */
export function calculateSkuPrices(prices: SkuPricesResponse, authenticated: boolean): SkuPriceStrings {
    const { cash, insurance } = prices
    const typicalCopay = prices.options?.find((o) => o.typical_copay != null)?.typical_copay ?? null
    const effectiveInsurance =
        insurance?.price != null ? insurance : typicalCopay != null ? { price: typicalCopay, primary: false } : null
    const insuranceLabel = authenticated && insurance?.price != null ? 'with your insurance' : 'typical copay'

    const hasCash = cash?.price != null
    const hasInsurance = effectiveInsurance?.price != null

    let preferredPrice: number | undefined
    let primaryText: string | undefined
    let secondaryPrice: number | undefined
    let secondaryText: string | undefined

    /* First choice → whatever backend flagged as "primary" */
    const primaryCandidate = [cash, effectiveInsurance].find((p) => p?.primary)

    if (primaryCandidate === cash && hasCash) {
        preferredPrice = cash!.price!
        primaryText = 'w/o insurance'

        if (hasInsurance) {
            secondaryPrice = effectiveInsurance!.price!
            secondaryText = insuranceLabel
        }
    } else if (primaryCandidate === effectiveInsurance && hasInsurance) {
        preferredPrice = effectiveInsurance!.price!
        primaryText = insuranceLabel

        if (hasCash) {
            secondaryPrice = cash!.price!
            secondaryText = 'w/o insurance'
        }
    } else {
        /* No "primary" flag found – fall back to cheaper price if both exist */
        if (hasCash && (!hasInsurance || cash!.price! <= effectiveInsurance!.price!)) {
            preferredPrice = cash!.price!
            primaryText = 'w/o insurance'
            if (hasInsurance) {
                secondaryPrice = effectiveInsurance!.price!
                secondaryText = insuranceLabel
            }
        } else if (hasInsurance) {
            preferredPrice = effectiveInsurance!.price!
            primaryText = insuranceLabel
            secondaryPrice = cash?.price ?? undefined
            secondaryText = secondaryPrice ? 'w/o insurance' : undefined
        }
    }

    return {
        preferredPrice,
        primaryText,
        secondaryPrice,
        secondaryPriceText: secondaryText,
        originalSkuPrices: prices,
    }
}

/**
 * Convert a numeric price (cents) + helper copy into a printable string.
 * Example: 1299 + "w/o insurance"  →  "$12.99 w/o insurance"
 */
function formatPrice(amount?: number, label?: string): string {
    if (amount == null || !label) return ''
    const price = amount / 100
    const humanizedPrice = Number.isInteger(price) ? price : price.toFixed(2)

    return `$${humanizedPrice} ${label}`
}

// NEW FOLLOW-UP VISIT PRICING DISPLAY
const BRAND_DISPLAY_MAP: Record<string, string> = {
    union_pay: 'UNION PAY',
    diners_club: 'DINERS',
    american_express: 'AMEX',
}

export function displayCardBrand(brand?: string | null): string {
    if (!brand) return ''

    const normalized = brand.toLowerCase()
    const mapped = BRAND_DISPLAY_MAP[normalized]
    if (mapped) return mapped

    // If it's a single "word" (no underscores/spaces), just all-caps it
    if (!normalized.includes('_') && !normalized.includes(' ')) return normalized.toUpperCase()

    // Otherwise, fall back to uppercasing the whole thing (keeps behavior predictable)
    return normalized.toUpperCase()
}

export interface UpdatedFollowUpVisitDisplayPricing {
    title: string
    subtitle: string
    buttonText: string
    icon: JSX.Element
    hasInsurance: boolean
    insuranceMoreExpensive: boolean
}

/**
 * Helper to extract prices from BookingOptions array
 */
function extractPricesFromBookingOptions(bookingOptions?: BookingOptions): {
    cashPriceCents: number | null
    mdsavePriceCents: number | null
    insurancePriceCents: number | null
} {
    if (!bookingOptions) return { cashPriceCents: null, mdsavePriceCents: null, insurancePriceCents: null }

    const cashOption = bookingOptions.find((opt) => opt.type === 'cash')
    const mdsaveOption = bookingOptions.find((opt) => opt.type === 'mdsave')
    const insuranceOption = bookingOptions.find((opt) => opt.type === 'insurance')

    return {
        cashPriceCents: cashOption ? parseInt(cashOption.price.amount, 10) : null,
        mdsavePriceCents: mdsaveOption ? parseInt(mdsaveOption.price.amount, 10) : null,
        insurancePriceCents: insuranceOption ? parseInt(insuranceOption.price.amount, 10) : null,
    }
}

/**
 * Get the cheapest billing method from booking options
 */
export function getCheapestBillingMethod(bookingOptions?: BookingOptions): SelectedPaymentOption | null {
    if (!bookingOptions || bookingOptions.length === 0) return null

    const sorted = [...bookingOptions].sort((a, b) => {
        const priceA = parseInt(a.price.amount, 10)
        const priceB = parseInt(b.price.amount, 10)
        return priceA - priceB
    })

    return sorted[0].type
}

/**
 * Check if booking options include an insurance option
 */
export function hasInsuranceOption(bookingOptions?: BookingOptions): boolean {
    return bookingOptions?.some((opt) => opt.type === 'insurance') ?? false
}

export function getUpdatedFollowUpVisitDisplay({
    bookingOptions,
    billingMethod,
}: {
    bookingOptions?: BookingOptions
    billingMethod: SelectedPaymentOption | null
}): UpdatedFollowUpVisitDisplayPricing {
    const { cashPriceCents, mdsavePriceCents, insurancePriceCents } = extractPricesFromBookingOptions(bookingOptions)

    // Get the non-insurance price based on selected billing method
    const nonInsurancePriceCents = billingMethod === 'mdsave' ? mdsavePriceCents : (cashPriceCents ?? mdsavePriceCents)

    const hasNonInsurance = nonInsurancePriceCents != null
    const hasInsurance = insurancePriceCents != null

    const insuranceNotPresent = !hasInsurance
    const insuranceMoreExpensive =
        hasNonInsurance && hasInsurance ? insurancePriceCents! > nonInsurancePriceCents! : false

    const title = getTitle()
    const subtitle = getSubtitle()
    const buttonText = getButtonText()
    const icon = insuranceNotPresent || insuranceMoreExpensive ? <CheckVerifiedIcon /> : <ShieldTickIcon />

    return {
        title,
        subtitle,
        buttonText,
        icon,
        hasInsurance,
        insuranceMoreExpensive,
    }

    function getTitle() {
        return billingMethod === 'insurance'
            ? `${formatCentsToDollars(insurancePriceCents ?? 0)} est. insurance price`
            : `${formatCentsToDollars(nonInsurancePriceCents ?? 0)} without insurance`
    }

    function getSubtitle() {
        let subtitle = ''
        if (billingMethod === 'insurance') {
            if (hasNonInsurance && hasInsurance) {
                const diff = nonInsurancePriceCents! - insurancePriceCents!
                if (diff > 0) subtitle = `You're saving ${formatCentsToDollars(diff)} with insurance`
                else if (diff < 0) subtitle = `${formatCentsToDollars(-diff)} more than paying without insurance`
                else subtitle = 'Same price as without insurance'
            }
        } else {
            if (!hasInsurance) subtitle = 'Guaranteed price'
            else if (hasNonInsurance && hasInsurance) {
                const diff = insurancePriceCents! - nonInsurancePriceCents!
                if (diff > 0) subtitle = `You're saving ${formatCentsToDollars(diff)} by paying without insurance`
                else if (diff < 0) subtitle = `${formatCentsToDollars(-diff)} more than insurance`
                else subtitle = 'Same price as using insurance'
            }
        }
        return subtitle
    }

    function getButtonText() {
        const selectedPriceCents = billingMethod === 'insurance' ? insurancePriceCents : nonInsurancePriceCents

        const alternativePriceCents = billingMethod === 'insurance' ? nonInsurancePriceCents : insurancePriceCents

        const selectedIsMoreExpensive =
            selectedPriceCents != null && alternativePriceCents != null && selectedPriceCents > alternativePriceCents

        return selectedIsMoreExpensive ? 'Change' : 'Edit'
    }
}
